import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';
import { inquiryApi } from '@/services/api';
import { BUSINESS_TYPES, DEFAULT_BUSINESS_TYPE } from '@/constants/businessTypes';
import { Store, Loader2, Check, AlertCircle, X, Eye, EyeOff } from 'lucide-react';
import { formatMoney } from '@/utils/currency';
import {
  COUNTRIES,
  CURRENCIES,
  getCountry,
  getRegionsForCountry,
  getAllUiLanguages,
  sortUiLanguagesForPicker,
  getTimezoneForCountry,
  isLanguageAllowed,
  isPakistan,
  type CurrencyCode,
} from '@/data/locales';
import { setAppLanguage } from '@/i18n';
import { normalizePhone, sanitizePhoneInput, validatePhone } from '@/utils/mobileValidation';
import { lookupUkPostcode } from '@/services/ukPostcode';
import { fetchClientGeo } from '@/utils/clientGeo';

interface PackageAddOn {
  name: string;
  add_on: string;
  description?: string;
  amount: number;
}

interface SaasPackage {
  package_name: string;
  package_code: string;
  monthly_price: number;
  quarterly_price?: number;
  annual_price?: number;
  trial_days: number;
  max_users: number;
  max_products: number;
  stock_module: number;
  service_module: number;
  reports_level: string;
  crm_support: number;
  priority_support: number;
  display_order: number;
  feature_list: string[];
  add_ons?: PackageAddOn[];
}

export default function SignupForm() {
  const { t } = useTranslation();
  // const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  // Success modal disabled since we now redirect
  // const [showSuccessModal, setShowSuccessModal] = useState(false);
  // const [successData, setSuccessData] = useState<{ company_code: string; username: string; message: string } | null>(null);
  // const [copiedField, setCopiedField] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [packages, setPackages] = useState<SaasPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', owner_name: '', email: '', mobile: '', city: '',
    province: '', postal_code: '',
    business_type: DEFAULT_BUSINESS_TYPE, subdomain: '', password: '', confirm_password: '', load_sample_data: true,
    sell_only_products: false,
    country: 'PK',
    currency: 'PKR' as CurrencyCode | string,
    ui_language: 'en',
    timezone: 'Asia/Karachi',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [cities, setCities] = useState<{ name: string, city: string }[]>([]);
  const [postcodeBusy, setPostcodeBusy] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState<'idle'|'checking'|'available'|'taken'|'too_short'>('idle');
  const [subdomainSuggestions, setSubdomainSuggestions] = useState<string[]>([]);
  const [referralCode, setReferralCode] = useState('');
  const [regionDetected, setRegionDetected] = useState(false);

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browser_language = navigator.language;
    void (async () => {
      const geo = await fetchClientGeo();
      try {
        const res = await inquiryApi.detectSignupRegion({
          timezone,
          browser_language,
          client_ip: geo.ip,
          client_country: geo.country,
          client_city: geo.city,
        });
        const data = res.data?.message;
        if (!data?.country) return;
        setRegionDetected(!!data.detected);
        setForm((prev) => ({
          ...prev,
          country: data.country || prev.country,
          currency: data.currency || prev.currency,
          ui_language: data.suggested_language || prev.ui_language,
          timezone: data.timezone || getTimezoneForCountry(data.country) || prev.timezone,
        }));
        if (data.suggested_language) {
          await setAppLanguage(data.suggested_language);
        }
      } catch {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('ref') || params.get('referral') || '').trim();
    if (code) {
      setReferralCode(code);
      try {
        sessionStorage.setItem('signup_referral_code', code);
      } catch {
        /* ignore */
      }
    } else {
      try {
        const stored = sessionStorage.getItem('signup_referral_code');
        if (stored) setReferralCode(stored);
      } catch {
        /* ignore */
      }
    }
  }, []);
  useEffect(() => {
    inquiryApi.getCities()
      .then(res => {
        if (res.data?.message) setCities(res.data.message);
      })
      .catch(err => console.error('Failed to load cities', err));
  }, []);

  useEffect(() => {
    setAppLanguage(form.ui_language, form.country);
  }, []);
  useEffect(() => {
    setPackagesLoading(true);
    inquiryApi.getPackages()
      .then(res => {
        if (res.data?.message) {
          const pkgs: SaasPackage[] = res.data.message;
          pkgs.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
          setPackages(pkgs);
          if (pkgs.length > 0) setSelectedPackage(pkgs[0].package_code);
        }
      })
      .catch(err => console.error('Failed to load packages', err))
      .finally(() => setPackagesLoading(false));
  }, []);

  const updateForm = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ---------- Validation Regex ----------
  const BUSINESS_NAME_REGEX = /^[A-Za-z0-9\s@.]+$/;
  const OWNER_NAME_REGEX = /^[A-Za-z\s]+$/;
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Relaxed password: min 6 chars
  const PASSWORD_REGEX = /^.{6,}$/;

  // ---------- Field-level validators ----------
  const validateBusinessName = (value: string) => {
    if (!value) return 'Business name is required';
    if (value.trim().length <= 3) return 'Business name must be more than 3 characters';
    if (!BUSINESS_NAME_REGEX.test(value)) return 'Only letters, numbers, spaces, @ and . are allowed';
    return '';
  };

  const validateOwnerName = (value: string) => {
    if (!value) return 'Owner name is required';
    if (value.trim().length <= 3) return 'Owner name must be more than 3 characters';
    if (!OWNER_NAME_REGEX.test(value)) return 'Only letters and spaces are allowed';
    return '';
  };

  const validateEmail = (value: string) => {
    if (!value) return 'Email is required';
    if (!EMAIL_REGEX.test(value)) return 'Enter a valid email address';
    return '';
  };

  const validateMobile = (value: string, country = form.country) => {
    return validatePhone(value, country, { required: true });
  };

  const validateCity = (value: string) => {
    if (!value) return 'City is required';
    return '';
  };

  const validateProvince = (value: string, country = form.country) => {
    if (country === 'GB' && !value) return 'Province / State / Region is required';
    return '';
  };

  const validatePostalCode = (value: string, country = form.country) => {
    if (country === 'GB' && !value.trim()) return 'Business postal code is required';
    return '';
  };

  const validateCountry = (value: string) => {
    if (!value) return 'Country is required';
    return '';
  };

  const validateCurrency = (value: string) => {
    if (!value) return 'Currency is required';
    return '';
  };

  const validateUiLanguage = (value: string) => {
    if (!value) return 'Language is required';
    if (!isLanguageAllowed(null, value)) return 'Selected language is not supported';
    return '';
  };

  const validatePassword = (value: string) => {
    if (!value) return 'Password is required';
    if (!PASSWORD_REGEX.test(value)) {
      return 'Password must be at least 6 characters long';
    }
    return '';
  };

  const validateConfirmPassword = (value: string, password: string) => {
    if (!value) return 'Please confirm your password';
    if (value !== password) return 'Passwords do not match';
    return '';
  };

  // Run validation for a single field and update fieldErrors
  const runFieldValidation = (field: string, value: string) => {
    let msg = '';
    switch (field) {
      case 'company_name':
        msg = validateBusinessName(value);
        break;
      case 'owner_name':
        msg = validateOwnerName(value);
        break;
      case 'email':
        msg = validateEmail(value);
        break;
      case 'mobile':
        msg = validateMobile(value);
        break;
      case 'city':
        msg = validateCity(value);
        break;
      case 'province':
        msg = validateProvince(value);
        break;
      case 'postal_code':
        msg = validatePostalCode(value);
        break;
      case 'country':
        msg = validateCountry(value);
        break;
      case 'currency':
        msg = validateCurrency(value);
        break;
      case 'ui_language':
        msg = validateUiLanguage(value);
        break;
      case 'password':
        msg = validatePassword(value);
        break;
      case 'confirm_password':
        msg = validateConfirmPassword(value, form.password);
        break;
      default:
        break;
    }
    setFieldErrors((prev) => ({ ...prev, [field]: msg }));
    return msg === '';
  };

  const handleCountryChange = (country: string) => {
    const meta = getCountry(country);
    setForm((prev) => ({
      ...prev,
      country,
      currency: meta.currency,
      timezone: getTimezoneForCountry(country),
      city: '',
      province: '',
      postal_code: '',
      mobile: '',
    }));
    setFieldErrors((prev) => ({
      ...prev,
      country: '',
      currency: '',
      ui_language: '',
      city: '',
      province: '',
      postal_code: '',
      mobile: '',
    }));
    setAppLanguage(form.ui_language, country);
  };

  const languageOptions = useMemo(() => {
    const withLabels = getAllUiLanguages().map((l) => ({
      ...l,
      label: t(`languages.${l.code}`, { defaultValue: l.label }),
    }));
    return sortUiLanguagesForPicker(withLabels, {
      selectedCode: form.ui_language,
      countryCode: form.country,
    });
  }, [form.ui_language, form.country, t]);

  const handleLanguageChange = (lang: string) => {
    setForm((prev) => ({ ...prev, ui_language: lang }));
    setFieldErrors((prev) => ({ ...prev, ui_language: validateUiLanguage(lang) }));
    setAppLanguage(lang, form.country);
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    if (field === 'mobile' && typeof value === 'string') {
      value = sanitizePhoneInput(value, form.country);
    }
    updateForm(field, value);
    if (typeof value === 'string') {
      runFieldValidation(field, value);
      // Re-check confirm password whenever password changes
      if (field === 'password' && form.confirm_password) {
        runFieldValidation('confirm_password', form.confirm_password);
      }
    }
  };

  const handleSubdomainBlur = async () => {
    if (!form.subdomain) {
      setSubdomainStatus('idle');
      setSubdomainSuggestions([]);
      return;
    }
    if (form.subdomain.length < 3) {
      setSubdomainStatus('too_short');
      setSubdomainSuggestions([]);
      return;
    }
    setSubdomainStatus('checking');
    try {
      const res = await inquiryApi.checkSubdomain(form.subdomain);
      const data = res.data.message;
      if (data.available) {
        setSubdomainStatus('available');
        setSubdomainSuggestions([]);
      } else {
        setSubdomainStatus('taken');
        setSubdomainSuggestions(data.suggestions || []);
      }
    } catch {
      setSubdomainStatus('idle');
    }
  };

  const validateStep1 = () => {
    const businessErr = validateBusinessName(form.company_name);
    const ownerErr = validateOwnerName(form.owner_name);
    const emailErr = validateEmail(form.email);
    const mobileErr = validateMobile(form.mobile);
    const cityErr = validateCity(form.city);
    const provinceErr = validateProvince(form.province);
    const postalErr = validatePostalCode(form.postal_code);
    const countryErr = validateCountry(form.country);
    const currencyErr = validateCurrency(form.currency);
    const languageErr = validateUiLanguage(form.ui_language);

    setFieldErrors((prev) => ({
      ...prev,
      company_name: businessErr,
      owner_name: ownerErr,
      email: emailErr,
      mobile: mobileErr,
      city: cityErr,
      province: provinceErr,
      postal_code: postalErr,
      country: countryErr,
      currency: currencyErr,
      ui_language: languageErr,
    }));

    return !businessErr && !ownerErr && !emailErr && !mobileErr && !cityErr
      && !provinceErr && !postalErr && !countryErr && !currencyErr && !languageErr;
  };

  const validateStep2 = () => {
    if (!selectedPackage) {
      setError('Please select a package to continue.');
      return false;
    }
    setError('');
    return true;
  };

  const getPackageDisplayPrice = (pkg: SaasPackage) => {
    if (pkg.monthly_price) return { price: pkg.monthly_price, cycle: 'mo' as const };
    if (pkg.annual_price) return { price: pkg.annual_price, cycle: 'year' as const };
    if (pkg.quarterly_price) return { price: pkg.quarterly_price, cycle: 'quarter' as const };
    return { price: 0, cycle: 'mo' as const };
  };

  const selectedPkg = packages.find((p) => p.package_code === selectedPackage);
  const packageDisplay = selectedPkg ? getPackageDisplayPrice(selectedPkg) : { price: 0, cycle: 'mo' as const };
  const selectedAddOnRows = (selectedPkg?.add_ons || []).filter((a) => selectedAddOns.includes(a.name));
  const addOnsTotal = selectedAddOnRows.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const signupTotal = packageDisplay.price + addOnsTotal;

  const toggleAddOn = (rowName: string) => {
    setSelectedAddOns((prev) =>
      prev.includes(rowName) ? prev.filter((id) => id !== rowName) : [...prev, rowName]
    );
  };

  const selectPackage = (packageCode: string) => {
    setSelectedPackage(packageCode);
    setSelectedAddOns([]);
  };

  const validateStep3 = () => {
    const passwordErr = validatePassword(form.password);
    const confirmErr = validateConfirmPassword(form.confirm_password, form.password);

    setFieldErrors((prev) => ({
      ...prev,
      password: passwordErr,
      confirm_password: confirmErr,
    }));

    if (!form.subdomain) {
      setError('Please choose a subdomain.');
      return false;
    }
    
    if (form.subdomain.length < 3) {
      setError('Subdomain must be at least 3 characters long.');
      return false;
    }

    if (subdomainStatus === 'taken') {
      setError('The selected subdomain is not available.');
      return false;
    }

    return !passwordErr && !confirmErr;
  };

  // ---------- Backend / Frappe error parsing ----------
  const parseErrorMessage = (err: unknown): string => {
    const error = err as {
      response?: {
        data?: {
          _server_messages?: string;
          exc_type?: string;
          exception?: string;
          message?: { message?: string; error?: string } | string;
        };
      };
      message?: string;
    };

    const data = error.response?.data;
    if (!data) return error.message || 'An error occurred. Please try again.';

    // Frappe sends _server_messages as a JSON-stringified array of JSON strings
    if (data._server_messages) {
      try {
        const messages: string[] = JSON.parse(data._server_messages);
        const parsedMessages = messages
          .map((m) => {
            try {
              const parsed = JSON.parse(m);
              return parsed.message || '';
            } catch {
              return m;
            }
          })
          .filter(Boolean);
        if (parsedMessages.length) return parsedMessages.join(' ');
      } catch {
        // fall through to other checks
      }
    }

    // Standard nested message object/string
    if (data.message) {
      if (typeof data.message === 'string') return data.message;
      if (data.message.message) return data.message.message;
      if (data.message.error) return data.message.error;
    }

    if (data.exception) return data.exception;
    if (data.exc_type) return `${data.exc_type}: Something went wrong`;

    return 'An error occurred. Please try again.';
  };

  // handleCopy removed

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateStep3()) return;
    setIsSubmitting(true);
    try {
      const res = await inquiryApi.submitSignup({
        ...form,
        country: form.country,
        currency: form.currency,
        ui_language: form.ui_language,
        timezone: form.timezone || getTimezoneForCountry(form.country),
        province: form.province || '',
        postal_code: form.postal_code || '',
        city: form.city || '',
        mobile: normalizePhone(form.mobile, form.country),
        package_code: selectedPackage,
        selected_add_ons: selectedAddOns,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });
      const result = res.data.message as {
        success?: boolean;
        company_code?: string;
        username?: string;
        message?: string;
        error?: string;
      };

      if (result?.success) {
        // Redirect user to their new subdomain login page
        window.location.href = `https://${form.subdomain}.saledesk.pk/login`;
      } else {
        const msg = result?.error || 'Signup failed. Please try again.';
        setError(msg);
        setShowErrorModal(true);
      }
    } catch (err: unknown) {
      const msg = parseErrorMessage(err);
      setError(msg);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card p-6 lg:p-8">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Store className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-xl font-bold text-gray-900">{t('signupForm.title')}</h2>
        <p className="text-sm text-gray-500">{t('signupForm.subtitle')}</p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-6">
        <div className={`flex-1 h-2 rounded-full transition-colors ${step >= 1 ? 'bg-primary-600' : 'bg-gray-200'}`} />
        <div className={`flex-1 h-2 rounded-full transition-colors ${step >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`} />
        <div className={`flex-1 h-2 rounded-full transition-colors ${step >= 3 ? 'bg-primary-600' : 'bg-gray-200'}`} />
      </div>
      <p className="text-xs text-gray-400 text-center -mt-4 mb-5">{t('signupForm.stepOf', { step })}</p>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.businessName')} *</label>
              <input
                required
                className="input-field"
                value={form.company_name}
                onChange={(e) => handleFieldChange('company_name', e.target.value)}
                placeholder={t('signupForm.businessNamePlaceholder')}
              />
              {fieldErrors.company_name && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.company_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.industry')} *</label>
              <select
                required
                className="input-field"
                value={form.business_type}
                onChange={(e) => handleFieldChange('business_type', e.target.value)}
              >
                {BUSINESS_TYPES.map((bt) => (
                  <option key={bt.value} value={bt.value}>{t(bt.labelKey)}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('signupForm.industryHint')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.country')} *</label>
                <select
                  required
                  className="input-field"
                  value={form.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{t(`countries.${c.code}`, { defaultValue: c.name })}</option>
                  ))}
                </select>
                {regionDetected && (
                  <p className="text-xs text-primary-600 mt-1">{t('signupForm.regionDetected')}</p>
                )}
                {fieldErrors.country && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.country}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.currency')} *</label>
                <select
                  required
                  className="input-field"
                  value={form.currency}
                  onChange={(e) => handleFieldChange('currency', e.target.value)}
                >
                  {Object.values(CURRENCIES).map((c) => (
                    <option key={c.code} value={c.code}>{c.code} ({c.symbol}) — {t(`currencies.${c.code}`, { defaultValue: c.name })}</option>
                  ))}
                </select>
                {fieldErrors.currency && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.currency}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.ownerName')} *</label>
              <input
                required
                className="input-field"
                value={form.owner_name}
                onChange={(e) => handleFieldChange('owner_name', e.target.value)}
                placeholder={t('signupForm.ownerNamePlaceholder')}
              />
              {fieldErrors.owner_name && (
                <p className="text-xs text-red-500 mt-1">{fieldErrors.owner_name}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.email')} *</label>
                <input
                  required
                  type="email"
                  className="input-field"
                  value={form.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder={t('signupForm.emailPlaceholder')}
                />
                {fieldErrors.email && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.mobile')} *</label>
                <input
                  required
                  type="tel"
                  inputMode="numeric"
                  maxLength={isPakistan(form.country) ? 11 : 15}
                  className="input-field"
                  value={form.mobile}
                  onChange={(e) => handleFieldChange('mobile', e.target.value)}
                  placeholder={isPakistan(form.country) ? t('signupForm.phonePlaceholderPk') : t('signupForm.phonePlaceholder')}
                />
                {fieldErrors.mobile && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.mobile}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.uiLanguage')} *</label>
                <select
                  required
                  className="input-field"
                  value={form.ui_language}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                >
                  {languageOptions.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
                {fieldErrors.ui_language && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.ui_language}</p>
                )}
              </div>
              {form.country === 'GB' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.province')} *</label>
                  <select
                    required
                    className="input-field"
                    value={form.province}
                    onChange={(e) => handleFieldChange('province', e.target.value)}
                  >
                    <option value="">{t('signupForm.selectProvince')}</option>
                    {getRegionsForCountry('GB').map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  {fieldErrors.province && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.province}</p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.city')} *</label>
                  {getCountry(form.country).useCityMaster ? (
                    <select required className="input-field" value={form.city} onChange={(e) => handleFieldChange('city', e.target.value)}>
                      <option value="">{t('signupForm.selectCity')}</option>
                      {cities.map((c) => (
                        <option key={c.name} value={c.name}>{c.city}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      required
                      className="input-field"
                      value={form.city}
                      onChange={(e) => handleFieldChange('city', e.target.value)}
                      placeholder={t('signupForm.cityPlaceholder')}
                    />
                  )}
                  {fieldErrors.city && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.city}</p>
                  )}
                </div>
              )}
            </div>
            {form.country === 'GB' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.postalCode')} *</label>
                  <div className="flex gap-2">
                    <input
                      required
                      className="input-field flex-1"
                      value={form.postal_code}
                      onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                      placeholder={t('signupForm.postalPlaceholderUk')}
                    />
                    <button
                      type="button"
                      className="btn-secondary text-xs px-3 whitespace-nowrap"
                      disabled={postcodeBusy || !form.postal_code.trim()}
                      onClick={async () => {
                        setPostcodeBusy(true);
                        try {
                          const r = await lookupUkPostcode(form.postal_code);
                          setForm((prev) => ({
                            ...prev,
                            postal_code: r.postcode,
                            province: r.province || prev.province,
                            city: r.city || prev.city,
                          }));
                          setFieldErrors((prev) => ({
                            ...prev,
                            postal_code: '',
                            province: r.province ? '' : prev.province,
                            city: r.city ? '' : prev.city,
                          }));
                        } catch (err) {
                          setFieldErrors((prev) => ({
                            ...prev,
                            postal_code: (err as Error).message || 'Postcode lookup failed',
                          }));
                        } finally {
                          setPostcodeBusy(false);
                        }
                      }}
                    >
                      {postcodeBusy ? '…' : t('signupForm.findPostcode')}
                    </button>
                  </div>
                  {fieldErrors.postal_code && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.postal_code}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.city')} *</label>
                  <input
                    required
                    className="input-field"
                    value={form.city}
                    onChange={(e) => handleFieldChange('city', e.target.value)}
                    placeholder={t('signupForm.cityFromPostcode')}
                  />
                  {fieldErrors.city && (
                    <p className="text-xs text-red-500 mt-1">{fieldErrors.city}</p>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.postalCode')}</label>
                <input
                  className="input-field"
                  value={form.postal_code}
                  onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                  placeholder={t('signupForm.postalPlaceholder')}
                />
                {fieldErrors.postal_code && (
                  <p className="text-xs text-red-500 mt-1">{fieldErrors.postal_code}</p>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.whatDoYouSell')} *</label>
              <select required className="input-field" value={form.sell_only_products ? 'only_products' : 'both'} onChange={(e) => handleFieldChange('sell_only_products', e.target.value === 'only_products')}>
                <option value="both">{t('signupForm.bothProductsServices')}</option>
                <option value="only_products">{t('signupForm.onlyProducts')}</option>
              </select>
            </div>
            <button type="button" onClick={() => validateStep1() && setStep(2)}
              className="btn-primary w-full mt-2">{t('signupForm.continue')}</button>
          </div>
        )}

        {/* ── Step 2: Package Selection ── */}
        {step === 2 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-1">{t('signupForm.choosePlan')}</p>
            <p className="text-xs text-gray-400 mb-4">{t('signupForm.trialNote', { days: packages[0]?.trial_days || 3 })}</p>

            {packagesLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                {packages.map((pkg) => {
                  const isSelected = selectedPackage === pkg.package_code;
                  const { price: displayPrice, cycle: displayCycle } = getPackageDisplayPrice(pkg);
                  return (
                    <div 
                      key={pkg.package_code}
                      className={`relative group border rounded-xl p-4 cursor-pointer transition-all ${
                        isSelected ? 'border-primary-600 bg-primary-50 shadow-sm' : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-gray-50'
                      }`}
                      onClick={() => selectPackage(pkg.package_code)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                          isSelected ? 'border-primary-600' : 'border-gray-300 group-hover:border-primary-400'
                        }`}>
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />}
                        </div>
                        <div className="flex-1 flex justify-between items-center">
                          <span className={`font-semibold ${isSelected ? 'text-primary-800' : 'text-gray-900'}`}>
                            {pkg.package_name}
                          </span>
                          <div className="text-right">
                            <span className="font-bold text-gray-900">{formatMoney(displayPrice, { currency: form.currency })}</span>
                            <span className="text-xs text-gray-500">/{displayCycle}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hover Details (Tooltip) */}
                      <div className="hidden group-hover:block absolute left-1/2 -translate-x-1/2 top-[calc(100%+8px)] w-[280px] bg-gray-900 text-white p-4 rounded-xl shadow-2xl z-50 animate-in fade-in zoom-in-95 duration-200 pointer-events-none">
                        <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 w-3 h-3 bg-gray-900 transform rotate-45"></div>
                        <div className="text-sm font-semibold mb-2 text-gray-100">{t('signupForm.packageFeatures')}</div>
                        <div className="flex flex-col gap-2">
                          {(pkg.feature_list || []).map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-1.5">
                              <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                              <span className="text-xs text-gray-300 leading-tight">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedPkg && (selectedPkg.add_ons?.length || 0) > 0 && (
              <div className="mb-5">
                <p className="text-sm font-semibold text-gray-700 mb-1">{t('signupForm.optionalAddons')}</p>
                <p className="text-xs text-gray-400 mb-3">{t('signupForm.enhancePlan', { plan: selectedPkg.package_name })}</p>
                <div className="space-y-2">
                  {selectedPkg.add_ons!.map((addon) => {
                    const checked = selectedAddOns.includes(addon.name);
                    return (
                      <label
                        key={addon.name}
                        className={`flex items-start gap-3 border rounded-xl p-3 cursor-pointer transition-all ${
                          checked ? 'border-primary-600 bg-primary-50' : 'border-gray-200 bg-white hover:border-primary-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={checked}
                          onChange={() => toggleAddOn(addon.name)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900">{addon.add_on}</span>
                            <span className="text-sm font-semibold text-gray-900 shrink-0">
                              +{formatMoney(addon.amount || 0, { currency: form.currency })}
                            </span>
                          </div>
                          {addon.description ? (
                            <p className="text-xs text-gray-500 mt-0.5">{addon.description}</p>
                          ) : null}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedPkg && (
              <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 space-y-1.5">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{selectedPkg.package_name}</span>
                  <span>{formatMoney(packageDisplay.price, { currency: form.currency })}/{packageDisplay.cycle}</span>
                </div>
                {selectedAddOnRows.map((addon) => (
                  <div key={addon.name} className="flex justify-between text-sm text-gray-600">
                    <span>{addon.add_on}</span>
                    <span>+{formatMoney(addon.amount || 0, { currency: form.currency })}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-semibold text-gray-900 pt-1.5 border-t border-gray-200">
                  <span>{t('signupForm.total')}</span>
                  <span>{formatMoney(signupTotal, { currency: form.currency })}/{packageDisplay.cycle}</span>
                </div>
                <p className="text-xs text-gray-400">{t('signupForm.trialFreeNote')}</p>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-500 mb-3 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </p>
            )}

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(1)} className="btn-secondary flex-1">{t('signupForm.back')}</button>
              <button
                type="button"
                onClick={() => validateStep2() && setStep(3)}
                className="btn-primary flex-1"
                disabled={!selectedPackage}
              >{t('signupForm.continue')}</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Account Setup ── */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.chooseSubdomain')}</label>
              <div className="flex">
                <input required className="input-field rounded-r-none border-r-0" value={form.subdomain}
                  onChange={(e) => {
                    updateForm('subdomain', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                    setSubdomainStatus('idle');
                  }}
                  onBlur={handleSubdomainBlur}
                  placeholder={t('signupForm.subdomainPlaceholder')} />
                <span className="bg-gray-100 border border-gray-200 border-l-0 rounded-r-xl px-3 py-2.5 text-sm text-gray-500 flex items-center">.saledesk.pk</span>
              </div>
              {subdomainStatus === 'checking' && <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin"/> {t('signupForm.checkingSubdomain')}</p>}
              {subdomainStatus === 'too_short' && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {t('signupForm.subdomainTooShort')}</p>}
              {subdomainStatus === 'available' && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> {t('signupForm.subdomainAvailable')}</p>}
              {subdomainStatus === 'taken' && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-xs text-amber-800 font-medium mb-2 flex items-center gap-1"><AlertCircle className="w-4 h-4"/> {t('signupForm.subdomainTaken')}</p>
                  <div className="flex flex-wrap gap-2">
                    {subdomainSuggestions.map(s => (
                      <button key={s} type="button" onClick={() => {
                        updateForm('subdomain', s);
                        setSubdomainStatus('available');
                        setSubdomainSuggestions([]);
                      }} className="px-2 py-1 bg-white border border-amber-200 text-amber-700 rounded text-xs hover:bg-amber-100 transition-colors font-medium">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.password')} *</label>
              <div className="relative">
                <input
                  required
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pr-10"
                  value={form.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  placeholder={t('signupForm.passwordPlaceholder')}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-xs text-red-500 mt-1">{fieldErrors.password}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('signupForm.confirmPassword')} *</label>
              <div className="relative">
                <input
                  required
                  type={showPassword ? 'text' : 'password'}
                  className="input-field pr-10"
                  value={form.confirm_password}
                  onChange={(e) => handleFieldChange('confirm_password', e.target.value)}
                  placeholder={t('signupForm.repeatPassword')}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                  {showPassword ? <EyeOff className="w-5 h-5 text-gray-500" /> : <Eye className="w-5 h-5 text-gray-500" />}
                </button>
              </div>
              {fieldErrors.confirm_password && <p className="text-xs text-red-500 mt-1">{fieldErrors.confirm_password}</p>}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={form.load_sample_data} onChange={(e) => updateForm('load_sample_data', e.target.checked)} className="rounded border-gray-300" />
              {t('signupForm.loadSampleData', { industry: t(BUSINESS_TYPES.find((bt) => bt.value === form.business_type)?.labelKey || 'industry.groceriesSupermarket') })}
            </label>
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">{t('signupForm.back')}</button>
              <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('signupForm.createAccount')}
              </button>
            </div>
          </div>
        )}
      </form>

      <p className="text-center text-sm text-gray-500 mt-4">
        {t('signupForm.alreadyHaveAccount')}{' '}
        <a href="/login" className="text-primary-600 font-medium hover:underline">
          {t('signupForm.signIn')}
        </a>
        {/* <Link to="/login" className="text-primary-600 font-medium hover:underline">Sign in</Link> */}
      </p>

      {/* Error Modal */}
      {showErrorModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowErrorModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowErrorModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">{t('signupForm.signupFailed')}</h3>
            <p className="text-sm text-gray-600 mb-5">{error}</p>
            <button
              type="button"
              onClick={() => setShowErrorModal(false)}
              className="btn-primary w-full"
            >
              {t('signupForm.okay')}
            </button>
          </div>
        </div>
      )}

      {/* Success Modal code removed */}
    </div>
  );
}